/**
 * Global audio player — one <audio> element at the AuthGate root that
 * survives page navigation. Library cards, render-sheet Player, and the
 * fixed bottom mini-bar all consume the same state via usePlayer().
 *
 * Why a context (not zustand/jotai): Vocaler doesn't ship a state lib;
 * the player state is small (current track + transport) so the cost
 * of context re-renders is fine. Plus context survives module reloads
 * in dev which makes the playback resilient across HMR.
 *
 * Why a single shared <audio>: it gives free mutex (browser audio
 * elements can't play two streams from one element), and the blob URL
 * stays bound across navigations so a play() on a different page
 * resumes the same track instead of re-fetching.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { services } from "../../services";

/** The minimum a track needs to be displayable in the mini-bar. */
export interface PlayerTrack {
  id: string;
  title: string;
  /** Free-form subtitle line (artist / model / brief.genre). Optional. */
  subtitle?: string;
}

interface PlayerState {
  /** Current loaded/playing/paused track, or null. */
  track: PlayerTrack | null;
  /** Live <audio> playing state. */
  isPlaying: boolean;
  /** Set while the next track's blob is being fetched. */
  loadingId: string | null;
  /** Current playhead position in seconds (live during playback). */
  currentTime: number;
  /** Track duration in seconds (set on loadedmetadata). */
  duration: number;
  /** Total length of all blob URLs we've minted, for debug. */
  _blobCount: number;
  /**
   * Last failure surface. Cleared by the next successful action. Set
   * on fetch failure (HTTP status), play() rejection (autoplay block,
   * codec issue), and audio onError. The mini-bar + render-sheet
   * player render this inline so the user sees something concrete
   * instead of a no-op click.
   */
  lastError: { id: string; message: string } | null;
}

interface PlayerActions {
  /**
   * Toggle play/pause for a track. If `id` matches the currently loaded
   * track, just toggle the transport. Otherwise fetch + bind + play.
   * Pass `meta` so the mini-bar shows real text instead of an id.
   */
  togglePlay: (id: string, meta?: Partial<PlayerTrack>) => Promise<void>;
  /** Pause the currently loaded track if any. */
  pause: () => void;
  /** Hard-stop: pause + clear track + drop loaded src. */
  stop: () => void;
  /** Seek to position in seconds. No-op if no track loaded. */
  seek: (seconds: number) => void;
}

type PlayerContextValue = PlayerState & PlayerActions;

const PlayerContext = createContext<PlayerContextValue | null>(null);

const INITIAL: PlayerState = {
  track: null,
  isPlaying: false,
  loadingId: null,
  currentTime: 0,
  duration: 0,
  _blobCount: 0,
  lastError: null,
};

export function PlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<PlayerState>(INITIAL);
  // Map of id -> blob URL so we can re-use the same blob when the user
  // toggles back to a track they already loaded (no second fetch).
  const blobCacheRef = useRef<Map<string, string>>(new Map());

  // Revoke all blobs on full provider unmount (app close). We keep them
  // alive across page navs deliberately so toggling back is instant.
  useEffect(() => {
    const cache = blobCacheRef.current;
    return () => {
      for (const url of cache.values()) URL.revokeObjectURL(url);
      cache.clear();
    };
  }, []);

  const togglePlay = useCallback<PlayerActions["togglePlay"]>(
    async (id, meta) => {
      const audio = audioRef.current;
      if (!audio || !id) return;
      // Same track loaded — just toggle transport.
      if (state.track?.id === id) {
        if (audio.paused) {
          try {
            await audio.play();
          } catch (e) {
            const message = e instanceof Error ? e.message : "Playback blocked";
            console.warn("[player] play() rejected", { id, message, error: e });
            setState((s) => ({ ...s, lastError: { id, message } }));
          }
        } else audio.pause();
        return;
      }
      // Clear stale errors when switching tracks.
      setState((s) => ({ ...s, lastError: null }));
      // Cache hit — bind + play immediately, no network roundtrip.
      const cached = blobCacheRef.current.get(id);
      if (cached) {
        setState((s) => ({
          ...s,
          track: { id, title: meta?.title ?? id, subtitle: meta?.subtitle },
        }));
        audio.src = cached;
        try {
          await audio.play();
        } catch (e) {
          const message = e instanceof Error ? e.message : "Playback blocked";
          console.warn("[player] cached play() rejected", { id, message, error: e });
          setState((s) => ({ ...s, lastError: { id, message } }));
        }
        return;
      }
      // Network path — sign the URL once, then let the <audio> tag
      // stream it via a normal Range-friendly GET. No blob round-trip
      // (the browser does its own progressive fetch + caching), no
      // cookie on the playback request (so iPad-Safari ITP can't
      // silently kill it). The signed URL is kept in the blob cache
      // map under the same key so re-plays in the same hour skip the
      // sign call too.
      setState((s) => ({ ...s, loadingId: id }));
      try {
        const signed = await services.renders.signUrl(id);
        blobCacheRef.current.set(id, signed.url);
        setState((s) => ({
          ...s,
          track: { id, title: meta?.title ?? id, subtitle: meta?.subtitle },
          _blobCount: blobCacheRef.current.size,
        }));
        // Bind src + play immediately. No setTimeout — the audio
        // element doesn't need React to flush; deferring just adds
        // race opportunities for the user to click again mid-load.
        audio.src = signed.url;
        try {
          await audio.play();
        } catch (e) {
          const message = e instanceof Error ? e.message : "Playback blocked";
          console.warn("[player] play() rejected after sign", { id, message, error: e });
          setState((s) => ({ ...s, lastError: { id, message } }));
        }
      } catch (e) {
        const message =
          e instanceof Error ? e.message : "Couldn't sign audio URL";
        console.warn("[player] sign failed", { id, message, error: e });
        setState((s) => ({ ...s, lastError: { id, message } }));
      } finally {
        setState((s) => ({ ...s, loadingId: null }));
      }
    },
    [state.track?.id],
  );

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    }
    setState((s) => ({ ...s, track: null, isPlaying: false, currentTime: 0, duration: 0 }));
  }, []);

  const seek = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (Number.isFinite(seconds) && seconds >= 0) {
      audio.currentTime = Math.min(seconds, audio.duration || seconds);
    }
  }, []);

  const value = useMemo<PlayerContextValue>(
    () => ({ ...state, togglePlay, pause, stop, seek }),
    [state, togglePlay, pause, stop, seek],
  );

  return (
    <PlayerContext.Provider value={value}>
      {children}
      {/* Single hidden audio element. Lives at provider root so it
          survives every page navigation under AuthGate. */}
      <audio
        ref={audioRef}
        preload="metadata"
        onPlay={() => setState((s) => ({ ...s, isPlaying: true }))}
        onPause={() => setState((s) => ({ ...s, isPlaying: false }))}
        onEnded={() => setState((s) => ({ ...s, isPlaying: false }))}
        onError={() =>
          setState((s) => ({ ...s, isPlaying: false, track: null }))
        }
        onLoadedMetadata={(e) => {
          const a = e.currentTarget;
          setState((s) => ({ ...s, duration: Number.isFinite(a.duration) ? a.duration : 0 }));
        }}
        onTimeUpdate={(e) => {
          const a = e.currentTarget;
          setState((s) => ({ ...s, currentTime: a.currentTime || 0 }));
        }}
        style={{ display: "none" }}
      />
    </PlayerContext.Provider>
  );
}

export function usePlayer(): PlayerContextValue {
  const ctx = useContext(PlayerContext);
  if (!ctx) {
    throw new Error("usePlayer must be used within a <PlayerProvider>");
  }
  return ctx;
}

/**
 * Per-card play state helper — returns just what a list/grid item needs
 * to render its play button. Memoised per-id so each card only re-renders
 * when its own state flips.
 */
export function useTrackPlayState(id: string): {
  active: boolean;
  playing: boolean;
  loading: boolean;
} {
  const { track, isPlaying, loadingId } = usePlayer();
  return useMemo(
    () => ({
      active: track?.id === id,
      playing: track?.id === id && isPlaying,
      loading: loadingId === id,
    }),
    [track?.id, isPlaying, loadingId, id],
  );
}
